import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';

// ReactQuill+React bug workaround
// https://github.com/zenoamaro/react-quill/issues/784
import DOMPurify from 'dompurify';
import ModalComponent from '../ModalComponent';
import QuillComponent from './QuillComponent';

const sanitizer = DOMPurify.sanitize;

type EditableTextUpdateHandler = (text: string) => void;

interface EditableTextProps {
  text: string
  isEditable: boolean
  updateHandler: EditableTextUpdateHandler
  open?: boolean
  textColor?: string
  textClassName?: string
  initialText?: string
  onChangeModalOpen?: (isOpen: boolean) => void
}

function EditableText({
  text,
  textColor,
  updateHandler,
  open: incomingOpen = false,
  isEditable,
  textClassName = 'ql-editor',
  onChangeModalOpen,
  initialText = '',
}: EditableTextProps) {
  const [open, setOpen] = useState(incomingOpen);
  const [value, setValue] = useState(text);
  const handleOpen = (event: React.MouseEvent) => {
    setOpen(true);
    onChangeModalOpen?.(true);
    event.stopPropagation();
  };
  const handleClose = () => {
    setOpen(false);
    onChangeModalOpen?.(false);
    setValue(text);
  };

  const applyText = () => {
    updateHandler(value);
    setOpen(false);
    onChangeModalOpen?.(false);
  };

  const resetText = (event: React.MouseEvent) => {
    setValue(null);
    event.stopPropagation();
  };

  useEffect(() => {
    setValue(text);
  }, [text]);

  useEffect(() => {
    if (incomingOpen) {
      setValue(text || initialText);
      setOpen(true);
    }
  }, [incomingOpen]);

  if (!isEditable) {
    return (
      <Box>
        <div
          className={`${textClassName} notranslate`}
          translate="no"
          dangerouslySetInnerHTML={{ __html: sanitizer(text) }}
          style={{ color: textColor }}
        />
      </Box>
    );
  }

  return (
    <>
      <Box onClick={(event) => event.stopPropagation()} className="ql-preview">
        <div
          role="presentation"
          className={`${textClassName} notranslate`}
          translate="no"
          onClick={handleOpen}
          dangerouslySetInnerHTML={{ __html: sanitizer(text) }}
          style={{ color: textColor, minHeight: '20px' }}
        />
      </Box>
      <ModalComponent
        size="lg"
        title="Text Settings"
        confirmButtonText="Apply"
        onConfirm={() => applyText()}
        show={open}
        closeModal={handleClose}
        centered
        animation={false}
        leftButtonSlot={
          <button type="button" className="btn btn-outline-secondary" onClick={resetText}>Reset</button>
        }
        backdropClassName="z-index-3000"
        style={{ zIndex: 3000 }}
      >
        <QuillComponent
          text={value}
          updateHandler={setValue}
        />
      </ModalComponent>
    </>
  );
}

export default EditableText;
